import { Alert, Box, Progress, Skeleton, useToast } from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';
import { isWipModified } from '@finley-technologies/credit-agreement-utils';

import {
  TAccountGQLType,
  useGetUserQuery,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { AdvanceRateTemplate } from './ValleyBankAdvanceRate/ui/details/AdvanceRateTemplate';
import { useGetBankAccountsWithInstitutionAndOwnerByImplementationIdQuery } from 'packages/bankAccounts/requests/bankAccounts.gql.generated';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useSimpleConfirm } from 'packages/common-frontend/hooks/useSimpleConfirm';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { CaWipExitPromptMessage } from 'packages/creditAgreements/ui/consts';
import {
  CreditAgreement,
  DbTable,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementFullArtifactSection } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/CreditAgreementFullArtifactSection';
import { CreditAgreementContextProvider } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks';
import { useSaveClientWipCreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks/useSaveClientWipCreditAgreement';
import { CreditAgreementWipStickyHeader } from 'packages/creditAgreements/ui/creditAgreementWip/CreditAgreementWipStickyHeader';
import {
  useWipConfig,
  getAllOrgProvidedInputKeysForUserAndFromWip,
  getOrganizationsFromWip,
} from 'packages/creditAgreements/ui/creditAgreementWip/hooks/useWipConfig';
import { createOrganizationMap } from 'packages/creditAgreements/ui/creditAgreementWip/utils/createMaps';
import { useGetCommonWipData } from 'packages/creditAgreements/ui/creditAgreementWip/utils/useGetCommonWipData';
import { useGetCurrentCaAndWip } from 'packages/creditAgreements/ui/creditAgreementWip/utils/useGetCurrentCaAndWip';
import { createWipSchemaValidation } from 'packages/creditAgreements/ui/creditAgreementWip/utils/wipSchemaValidation';
import {
  LoanProductsMap,
  OrganizationMap,
} from 'packages/creditAgreements/ui/types';
import { useGetOrgProvidedInputsByOrgIdQuery } from 'packages/orgProvidedInputs/requests/requests.gql.generated';
import { SettingsFacilityBorrowingBaseTemplate } from 'packages/settings/ui/SettingsFacilityBorrowingBaseTemplate';
import { SettingsFacilityPortfolioCovenantsTemplate } from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsTemplate';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

type CreditAgreementSectionTemplateProps = {
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  children: React.ReactNode;
  creditFacilityId: string;
  isEditOnlyMode?: boolean;
};
const CreditAgreementSectionTemplate = ({
  section,
  creditFacilityId,
  children,
  isEditOnlyMode,
}: CreditAgreementSectionTemplateProps) => {
  if (section === 'borrowingBaseMechanics') {
    return (
      <SettingsFacilityBorrowingBaseTemplate
        creditFacilityId={creditFacilityId}
        selectedTab="configuration"
      >
        {children}
      </SettingsFacilityBorrowingBaseTemplate>
    );
  }

  if (section === 'portfolioCovenants' && !isEditOnlyMode) {
    return (
      <SettingsFacilityPortfolioCovenantsTemplate
        creditFacilityId={creditFacilityId}
      >
        {children}
      </SettingsFacilityPortfolioCovenantsTemplate>
    );
  }

  return <>{children}</>;
};

type CreditAgreementLoadingSkeletonProps = {
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  creditFacilityId: string;
  isEditOnlyMode?: boolean;
};
const CreditAgreementLoadingSkeleton = ({
  isEditOnlyMode,
  section,
  creditFacilityId,
}: CreditAgreementLoadingSkeletonProps) => {
  if (section === 'portfolioCovenants' && isEditOnlyMode) {
    return (
      <Box>
        <SettingsFacilityPortfolioCovenantsTemplate
          creditFacilityId={creditFacilityId}
        >
          <Alert status="info" mt="24px">
            {`Loading ${commonUtils.toTitleCase(section)}...`}
          </Alert>
          <Progress size="xs" isIndeterminate />
        </SettingsFacilityPortfolioCovenantsTemplate>
      </Box>
    );
  }
  return (
    <Box p={isEditOnlyMode ? '40px' : undefined}>
      <CreditAgreementSectionTemplate
        creditFacilityId={creditFacilityId}
        section={section}
        isEditOnlyMode={isEditOnlyMode}
      >
        <Alert status="info" mt="24px">
          {`Loading ${commonUtils.toTitleCase(section)}...`}
        </Alert>
        <Progress size="xs" isIndeterminate />
      </CreditAgreementSectionTemplate>
    </Box>
  );
};

type CreditAgreementFullArtifactSectionWithIntrospectionEntityFetchProps = {
  organizationId: string;
  creditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  organizationMap: OrganizationMap;
  tAccounts: TAccountGQLType[];
  facilityType: DbTable.FacilityType;
  implementationId: string;
  formProps: FormikProps<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>;
  creditFacilityId: string;
  arePartiesLocked: boolean;
  setArePartiesLocked: React.Dispatch<React.SetStateAction<boolean>>;
  loanProductsMap: LoanProductsMap;
  operationMode: CreditAgreement.OperationMode;
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  isEditOnlyMode?: boolean;
};
/**
 * introspection entity fetch parallels "CreditAgreementFullArtifactWithDynamicEntityFetch"
 * on admin dashboard, but instantiates wip config interface with information
 * already existing within the CA wip, as lender cannot have full access to all
 * organizations, users, and bank accounts
 */
const CreditAgreementFullArtifactSectionWithIntrospectionEntityFetch = ({
  organizationId,
  creditAgreementFullArtifact,
  organizationMap,
  tAccounts,
  facilityType,
  implementationId,
  formProps,
  creditFacilityId,
  arePartiesLocked,
  setArePartiesLocked,
  loanProductsMap,
  operationMode,
  section,
  isEditOnlyMode = false,
}: CreditAgreementFullArtifactSectionWithIntrospectionEntityFetchProps) => {
  const { user } = useUserAuthOrThrow();
  const [isFormDirty, setIsFormDirty] = useState(formProps.dirty);

  useEffect(() => {
    if (isFormDirty !== formProps.dirty) {
      setIsFormDirty(formProps.dirty);
    }
  }, [formProps, isFormDirty]);

  const {
    saveAndUpdateConfigCreditAgreementWip,
    saveCreditAgreementFullArtifactWip,
    isSaveCreditAgreementFullArtifactWipLoading,
  } = useSaveClientWipCreditAgreement({ creditFacilityId });

  // !TODO @eldarshamukhamedov react-router@6: This needs to be replaced with unstable_usePrompt as
  // soon as we switch to a data router.
  useSimpleConfirm({
    when: isFormDirty,
    message: CaWipExitPromptMessage,
  });

  // get org provided inputs data for user organization
  // users should be able to access their own organization's org provided inputs
  const { data: orgProvidedInputsData } = useGetOrgProvidedInputsByOrgIdQuery({
    variables: { organizationId: user.organizationId },
  });

  const {
    data: bankAccountsWithInstitutionsData,
  } = useGetBankAccountsWithInstitutionAndOwnerByImplementationIdQuery({
    variables: { implementationId },
  });

  // Calculate org provided input keys - use empty array as fallback if data not available yet
  const orgProvidedInputKeys = orgProvidedInputsData
    ? getAllOrgProvidedInputKeysForUserAndFromWip({
        userOrgProvidedInputs:
          orgProvidedInputsData.getOrgProvidedInputsByOrgId,
        creditAgreementWip: formProps.values,
      })
    : [];

  // Use the reusable hook for wipConfig creation
  const { wipConfig } = useWipConfig({
    creditAgreementFullArtifact,
    formProps,
    organizationMap,
    tAccounts,
    loanProductsMap,
    facilityType,
    creditFacilityId,
    arePartiesLocked,
    setArePartiesLocked,
    operationMode,
    saveCreditAgreementFullArtifactWip,
    isSaveCreditAgreementFullArtifactWipLoading,
    saveAndUpdateConfigCreditAgreementWip,
    orgProvidedInputKeys,
    bankAccountsWithInstitutionAndOwner:
      bankAccountsWithInstitutionsData?.implementation
        .bankAccountsWithInstitutionAndOwner ?? [],
  });

  // Early return after all hooks are called
  if (!orgProvidedInputsData || !bankAccountsWithInstitutionsData) {
    return <Skeleton />;
  }

  return (
    <CreditAgreementFullArtifactSection
      organizationId={organizationId}
      creditAgreementFullArtifact={creditAgreementFullArtifact}
      section={section}
      wipConfig={wipConfig}
      isEditOnlyMode={isEditOnlyMode}
    />
  );
};

type CreditAgreementDocumentSectionWipProps = {
  creditAgreementFullArtifactWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  currentCreditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  isEditOnlyMode?: boolean;
};
const CreditAgreementDocumentSectionWip = ({
  creditAgreementFullArtifactWip,
  currentCreditAgreementFullArtifact,
  section,
  isEditOnlyMode,
}: CreditAgreementDocumentSectionWipProps) => {
  const flags = useFeatureFlags();
  const navigate = useNavigate();
  const toast = useToast();

  const [isDiffOpen, setIsDiffOpen] = useState<boolean>(false);
  const [isJsonUploadOpen, setIsJsonUploadOpen] = useState(false);
  const [arePartiesLocked, setArePartiesLocked] = useState(true);

  const { creditFacilityId } = creditAgreementFullArtifactWip;

  // wip has already been modified by BE to contain latest artifactDocumentId
  const artifactDocumentId =
    creditAgreementFullArtifactWip.borrowingBaseMechanics.calcAgentMechanics
      ._artifactDocumentId_ || null;

  const {
    implementationId,
    creditFacilitySlug,
    tAccounts,
    loanProductsMap,
    facilityType,
  } = useGetCommonWipData({
    creditFacilityId,
  });

  const {
    saveCreditAgreementFullArtifactWip,
    isSaveCreditAgreementFullArtifactWipLoading,
  } = useSaveClientWipCreditAgreement({ creditFacilityId });

  const isWipDirtyCallback = useCallback(
    (newWip: CreditAgreement.DomainEntity.CreditAgreementFullArtifact) => {
      return isWipModified(creditAgreementFullArtifactWip, newWip);
    },
    [creditAgreementFullArtifactWip],
  );

  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  if (
    !implementationId ||
    !creditFacilitySlug ||
    !tAccounts ||
    !loanProductsMap ||
    !facilityType ||
    !userData ||
    !userData.user
  ) {
    return <Skeleton />;
  }

  // orgId of user on client dashboard
  // Client CA Wip also user own org provided input keys in addition to introspection
  const organizationId = userData.user.organizationId;

  const organizations = getOrganizationsFromWip(creditAgreementFullArtifactWip);
  const organizationMap = createOrganizationMap(organizations);

  const operationMode: CreditAgreement.OperationMode = 'client';

  const onCommitDone = () => {
    if (!flags.isValleyBankLenderView) {
      navigate('/settings/credit-facilities');
    } else {
      navigate('/');
    }
  };

  // @vb save wip permission not explicitly used because there is no read only role type

  // @vb this is the cleanest way to determine if the user can commit the credit agreement without having to prop drill permissions down
  // need to rethink the best way to handle this
  const isCreditAgreementCommitAllowed = flags.isValleyBankLenderView
    ? userData.user.roleType === 'ADMIN' // only finley & stacey can commit for valley bank lender view
    : true;

  return (
    <Formik
      initialValues={creditAgreementFullArtifactWip}
      onSubmit={() => {}}
      validationSchema={createWipSchemaValidation()}
      validateOnChange
      validateOnMount
      enableReinitialize // allows the entire credit agreement formik initialValues to be reset to current values upon rerender (eg. on saves)
    >
      {(formProps) => {
        const isWipDirty = isWipDirtyCallback(formProps.values);

        return (
          <Form
            style={{
              display: 'flex',
              height: '100%',
              overflowY: 'auto',
            }}
          >
            <CreditAgreementContextProvider
              initOrgSlugs={organizations.map(({ slug }) => slug)}
              parties={creditAgreementFullArtifactWip.facilityMechanics.parties}
            >
              {/* maxH required for second level sticky header to work
                since position:sticky attaches to the nearest ancestor with a scrolling mechanism
                https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll */}
              <Box overflowY="auto" maxH="100vh" width="100%">
                <CreditAgreementWipStickyHeader
                  isWipDirty={isWipDirty}
                  isDiffOpen={isDiffOpen}
                  setIsDiffOpen={setIsDiffOpen}
                  creditFacilityId={creditFacilityId}
                  formProps={formProps}
                  artifactDocumentId={artifactDocumentId}
                  creditFacilitySlug={creditFacilitySlug}
                  onCommitDone={onCommitDone}
                  currentCreditAgreementFullArtifact={
                    currentCreditAgreementFullArtifact
                  }
                  isJsonUploadOpen={isJsonUploadOpen}
                  setIsJsonUploadOpen={setIsJsonUploadOpen}
                  operationMode={operationMode}
                  saveCreditAgreementFullArtifactWip={
                    saveCreditAgreementFullArtifactWip
                  }
                  isSaveCreditAgreementFullArtifactWipLoading={
                    isSaveCreditAgreementFullArtifactWipLoading
                  }
                  isCommitAllowed={isCreditAgreementCommitAllowed}
                />

                {flags.isValleyBankLenderView ? (
                  <AdvanceRateTemplate>
                    <CreditAgreementFullArtifactSectionWithIntrospectionEntityFetch
                      organizationId={organizationId}
                      creditAgreementFullArtifact={formProps.values}
                      organizationMap={organizationMap}
                      creditFacilityId={creditFacilityId}
                      implementationId={implementationId}
                      formProps={formProps}
                      arePartiesLocked={arePartiesLocked}
                      setArePartiesLocked={setArePartiesLocked}
                      tAccounts={tAccounts}
                      loanProductsMap={loanProductsMap}
                      operationMode={operationMode}
                      section={section}
                      facilityType={facilityType}
                    />
                  </AdvanceRateTemplate>
                ) : (
                  <CreditAgreementSectionTemplate
                    creditFacilityId={creditFacilityId}
                    section={section}
                    isEditOnlyMode={isEditOnlyMode}
                  >
                    <CreditAgreementFullArtifactSectionWithIntrospectionEntityFetch
                      organizationId={organizationId}
                      creditAgreementFullArtifact={formProps.values}
                      organizationMap={organizationMap}
                      creditFacilityId={creditFacilityId}
                      implementationId={implementationId}
                      formProps={formProps}
                      arePartiesLocked={arePartiesLocked}
                      setArePartiesLocked={setArePartiesLocked}
                      tAccounts={tAccounts}
                      loanProductsMap={loanProductsMap}
                      operationMode={operationMode}
                      section={section}
                      isEditOnlyMode={isEditOnlyMode}
                      facilityType={facilityType}
                    />
                  </CreditAgreementSectionTemplate>
                )}
              </Box>
            </CreditAgreementContextProvider>
          </Form>
        );
      }}
    </Formik>
  );
};

type CreditAgreementSectionWipProps = {
  creditFacilityId: string;
  section: keyof CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  isEditOnlyMode?: boolean;
};
export const CreditAgreementSectionWip = ({
  section,
  creditFacilityId,
  isEditOnlyMode,
}: CreditAgreementSectionWipProps) => {
  const {
    creditAgreementFullArtifactWip,
    currentCreditAgreementFullArtifact,
  } = useGetCurrentCaAndWip({
    creditFacilityId,
    creditAgreementsPath: '/credit-agreements',
  });

  if (!creditAgreementFullArtifactWip || !currentCreditAgreementFullArtifact) {
    return (
      <CreditAgreementLoadingSkeleton
        creditFacilityId={creditFacilityId}
        section={section}
        isEditOnlyMode={isEditOnlyMode}
      />
    );
  }

  return (
    <CreditAgreementDocumentSectionWip
      creditAgreementFullArtifactWip={creditAgreementFullArtifactWip}
      currentCreditAgreementFullArtifact={currentCreditAgreementFullArtifact}
      section={section}
      isEditOnlyMode={isEditOnlyMode}
    />
  );
};
